import { isNetworkError } from "@ronin/api-client";
import type { AuthStatus } from "@ronin/contracts";
import { CalendarView, Id, IsoDate } from "@ronin/contracts";
import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { authStatusQuery } from "./api.js";
import { AppShell } from "./components/app-shell.js";
import { isNative } from "./platform/native.js";
import { offlineUser, openOfflineStore } from "./platform/offline/index.js";
import { serverUrl } from "./platform/server.js";
import { CalendarPage } from "./routes/calendar.js";
import { ConnectPage } from "./routes/connect.js";
import { HomePage } from "./routes/home.js";
import { LoginPage } from "./routes/login.js";
import { SettingsPage } from "./routes/settings.js";
import { SetupPage } from "./routes/setup.js";
import { TodosPage } from "./routes/todos.js";

interface RouterContext {
  queryClient: QueryClient;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({ component: Outlet });

/** spec08 FR5: the app needs a server address before it can ask anything. */
function requireServer() {
  if (isNative && !serverUrl()) throw redirect({ to: "/connect" });
}

const connectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/connect",
  beforeLoad: () => {
    if (!isNative) throw redirect({ to: "/login" });
  },
  component: ConnectPage,
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  beforeLoad: async ({ context }) => {
    requireServer();
    const status = await context.queryClient.ensureQueryData(authStatusQuery);
    if (!status.setupRequired) throw redirect({ to: "/login" });
  },
  component: SetupPage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  beforeLoad: async ({ context }) => {
    requireServer();
    // Offline the form still shows; signing in then says there is no connection.
    const status = await context.queryClient
      .ensureQueryData(authStatusQuery)
      .catch((error: unknown) => {
        if (isNetworkError(error)) return null;
        throw error;
      });
    if (!status) return;
    if (status.setupRequired) throw redirect({ to: "/setup" });
    if (status.user) throw redirect({ to: "/" });
  },
  component: LoginPage,
});

/**
 * The signed-in user. In the app (spec11) this also opens the user's offline copy, and
 * without a connection the app starts from that copy (FR16).
 */
async function appStatus(queryClient: QueryClient): Promise<AuthStatus> {
  if (!isNative) return queryClient.ensureQueryData(authStatusQuery);
  try {
    const status = await queryClient.ensureQueryData(authStatusQuery);
    if (status.user) await openOfflineStore(status.user);
    return status;
  } catch (error) {
    const user = isNetworkError(error) ? await offlineUser() : null;
    if (!user) throw error;
    return { setupRequired: false, user };
  }
}

/** Everything below requires a signed-in user. */
const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: async ({ context }) => {
    requireServer();
    const status = await appStatus(context.queryClient);
    if (status.setupRequired) throw redirect({ to: "/setup" });
    if (!status.user) throw redirect({ to: "/login" });
    return { user: status.user };
  },
  component: AppShell,
});

interface HomeSearch {
  board?: string | undefined;
}

/** spec12 FR1: an invalid board id falls back to the first board (see calendarSearch). */
function homeSearch(search: Record<string, unknown>): HomeSearch {
  return { board: Id.safeParse(search.board).data };
}

const homeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  validateSearch: homeSearch,
  beforeLoad: () => {
    // spec11 FR16: offline, the app opens where it still works (the dashboard is online-only).
    if (isNative && !navigator.onLine) throw redirect({ to: "/notes" });
  },
  component: HomePage,
});

const todosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/todos",
  component: TodosPage,
});

interface CalendarSearch {
  view?: CalendarView | undefined;
  date?: string | undefined;
}

/**
 * An invalid view or date in the URL falls back to the default instead of an error page.
 * Both keys are always returned: the router merges this over the raw search, so a left-out
 * key would keep the invalid raw value.
 */
function calendarSearch(search: Record<string, unknown>): CalendarSearch {
  return {
    view: CalendarView.safeParse(search.view).data,
    date: IsoDate.safeParse(search.date).data,
  };
}

const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calendar",
  validateSearch: calendarSearch,
  component: CalendarPage,
});

interface NotesSearch {
  view?: "all" | "pinned" | "trash" | undefined;
  notebook?: string | undefined;
  note?: string | undefined;
}

/** Unknown values fall back to "All notes" (all keys returned, see calendarSearch). */
function notesSearch(search: Record<string, unknown>): NotesSearch {
  const view = search.view;
  return {
    view: view === "all" || view === "pinned" || view === "trash" ? view : undefined,
    notebook: Id.safeParse(search.notebook).data,
    note: Id.safeParse(search.note).data,
  };
}

const notesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notes",
  validateSearch: notesSearch,
  // The editor (CodeMirror, KaTeX) is large: load it only when notes are opened.
  component: lazyRouteComponent(() => import("./routes/notes.js"), "NotesPage"),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  connectRoute,
  setupRoute,
  loginRoute,
  appRoute.addChildren([homeRoute, todosRoute, calendarRoute, notesRoute, settingsRoute]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent" });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
